import axios from 'axios';

// Read backend configuration from environment variables with fallbacks
const BACKEND_HOST = process.env.REACT_APP_BACKEND_HOST || 'localhost';
const BACKEND_PORT = process.env.REACT_APP_BACKEND_PORT || '8000';
const DEBUG = process.env.REACT_APP_DEBUG || 'True';

let API_BASE_URL;
if (DEBUG === 'True') {
  API_BASE_URL = `http://${BACKEND_HOST}:${BACKEND_PORT}/api`;
} else {
  // Ensure HTTPS is used in production to prevent mixed content errors
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://api-invoice-hub.ekbana.ai/api';
  API_BASE_URL = backendUrl.replace(/^http:/, 'https:');
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if the request is a login request to avoid duplicate error handling for auth errors
    const isLoginRequest = error.config?.url?.includes('/login');
    
    // For network errors (no connection), we always want to show them regardless of request type
    if (!error.response) {
      // Network error (no connection to backend) - always dispatch for all requests
      const errorEvent = new CustomEvent('apiError', {
        detail: {
          message: 'No connection to backend server. Please check your internet connection.',
          type: 'error',
          source: 'network'
        }
      });
      window.dispatchEvent(errorEvent);
    } else if ([502, 503, 504].includes(error.response.status)) {
      // Backend server unavailable - always dispatch for all requests
      const errorEvent = new CustomEvent('apiError', {
        detail: {
          message: 'No connection to backend server. Service is temporarily unavailable.',
          type: 'error',
          source: 'network'
        }
      });
      window.dispatchEvent(errorEvent);
    } else if (error.response.status === 401 && !isLoginRequest) {
      // Token might be expired, redirect to login - but only for non-login requests
      // For login requests, 401 means invalid credentials and should be handled by the calling function
      localStorage.removeItem('token');
      window.location.href = '/login';
    } else if (!isLoginRequest) {
      // For non-login requests, dispatch other server errors
      // This avoids duplicate messages for authentication failures on login
      const errorEvent = new CustomEvent('apiError', {
        detail: {
          message: error.response.data?.detail || `Request failed with status ${error.response.status}.`,
          type: 'error',
          source: 'server'
        }
      });
      window.dispatchEvent(errorEvent);
    }
    return Promise.reject(error);
  }
);

// Authentication API calls
export const authAPI = {
  login: (username, password) => {
    // Use the new login endpoint for external auth
    return api.post('/login', {
      username: username,
      password: password
    });
  },
  register: (userData) => api.post('/users', userData),
};

// Organization API calls
export const organizationAPI = {
  createOrganization: (orgData) => api.post('/organizations', orgData),
  getOrganizations: (skip = 0, limit = 100) => api.get(`/organizations?skip=${skip}&limit=${limit}`),
  getOrganization: (id) => api.get(`/organizations/${id}`),
  updateOrganization: (id, orgData) => api.put(`/organizations/${id}`, orgData),
  deleteOrganization: (id) => api.delete(`/organizations/${id}`),
  getOrganizationWithUsers: (id) => api.get(`/organizations/${id}/with-users`),
  getOrganizationWithAllData: (id) => api.get(`/organizations/${id}/with-all-data`),
};

// Invoice API calls
export const invoiceAPI = {
  getAvailableYears: () => api.get('/invoices/available-years'),
  getInvoices: (skip = 0, limit = 100, vendorId = null, year = null, sortBy = null, sortOrder = null) => {
    let url = `/invoices?skip=${skip}&limit=${limit}`;
    if (vendorId) {
      url += `&vendor_id=${vendorId}`;
    }
    if (year) {
      url += `&year=${year}`;
    }
    if (sortBy) {
      url += `&sort_by=${sortBy}`;
    }
    if (sortOrder) {
      url += `&sort_order=${sortOrder}`;
    }
    return api.get(url);
  },
  getInvoice: (id) => api.get(`/invoices/${id}`),
  getInvoicePdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  uploadInvoice: (file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    // Create a temporary axios instance that doesn't have the default JSON content-type
    const tempApi = axios.create({
      baseURL: API_BASE_URL,
    });

    // Apply interceptors to maintain auth and error handling
    tempApi.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Don't set content-type for multipart/form-data - let browser handle it
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    tempApi.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if it's a network error or backend connection issue
        if (!error.response) {
          // Network error (no connection to backend)
          const errorEvent = new CustomEvent('apiError', {
            detail: {
              message: 'No connection to backend server. Please check your internet connection.',
              type: 'error',
              source: 'network'
            }
          });
          window.dispatchEvent(errorEvent);
        } else if ([502, 503, 504].includes(error.response.status)) {
          // Backend server unavailable
          const errorEvent = new CustomEvent('apiError', {
            detail: {
              message: 'No connection to backend server. Service is temporarily unavailable.',
              type: 'error',
              source: 'network'
            }
          });
          window.dispatchEvent(errorEvent);
        } else if (error.response.status === 401) {
          // Token might be expired, redirect to login
          localStorage.removeItem('token');
          window.location.href = '/login';
        } else {
          // Other server errors - just pass them through for specific handling
          const errorEvent = new CustomEvent('apiError', {
            detail: {
              message: error.response.data?.detail || `Request failed with status ${error.response.status}.`,
              type: 'error',
              source: 'server'
            }
          });
          window.dispatchEvent(errorEvent);
        }
        return Promise.reject(error);
      }
    );

    // Make the request with progress tracking
    // The backend processes the file completely before responding, so the success/error
    // will reflect the final state (after data extraction and duplicate check)
    return tempApi.post('/invoices/upload', formData, {
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress) {
          onUploadProgress(progressEvent);
        }
      }
    });
  },
  uploadBulkInvoices: (files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    // Create a temporary axios instance that doesn't have the default JSON content-type
    const tempApi = axios.create({
      baseURL: API_BASE_URL,
    });

    // Apply interceptors to maintain auth and error handling
    tempApi.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Don't set content-type for multipart/form-data - let browser handle it
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    tempApi.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if it's a network error or backend connection issue
        if (!error.response) {
          // Network error (no connection to backend)
          const errorEvent = new CustomEvent('apiError', {
            detail: {
              message: 'No connection to backend server. Please check your internet connection.',
              type: 'error',
              source: 'network'
            }
          });
          window.dispatchEvent(errorEvent);
        } else if ([502, 503, 504].includes(error.response.status)) {
          // Backend server unavailable
          const errorEvent = new CustomEvent('apiError', {
            detail: {
              message: 'No connection to backend server. Service is temporarily unavailable.',
              type: 'error',
              source: 'network'
            }
          });
          window.dispatchEvent(errorEvent);
        } else if (error.response.status === 401) {
          // Token might be expired, redirect to login
          localStorage.removeItem('token');
          window.location.href = '/login';
        } else {
          // Other server errors - just pass them through for specific handling
          const errorEvent = new CustomEvent('apiError', {
            detail: {
              message: error.response.data?.detail || `Request failed with status ${error.response.status}.`,
              type: 'error',
              source: 'server'
            }
          });
          window.dispatchEvent(errorEvent);
        }
        return Promise.reject(error);
      }
    );

    return tempApi.post('/invoices/upload-bulk', formData);
  },
  updateInvoice: (id, invoiceData) => api.put(`/invoices/${id}`, invoiceData),
  deleteInvoice: (id) => {
    // Clear any potential cached invoice data from local storage before deletion
    const cachedInvoiceKeys = Object.keys(localStorage).filter(key => 
      key.startsWith('invoice_') || key.startsWith('invoices_')
    );
    cachedInvoiceKeys.forEach(key => localStorage.removeItem(key));
    
    return api.delete(`/invoices/${id}`);
  },
};

// Vendor API calls
export const vendorAPI = {
  getVendors: () => api.get('/vendors'),
  createVendor: (vendorData) => api.post('/vendors', vendorData),
  updateVendor: (vendorId, vendorData) => api.put(`/vendors/${vendorId}`, vendorData),
};

// Category API calls
export const categoryAPI = {
  getCategories: () => api.get('/categories'),
  createCategory: (categoryData) => api.post('/categories', categoryData),
  updateCategory: (categoryId, categoryData) => api.put(`/categories/${categoryId}`, categoryData),
};

// Dashboard API calls
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getCostGraph: (timeRange = 'monthly', vendorId = null, categoryId = null) => {
    let url = `/dashboard/charts/cost-graph?time_range=${timeRange}`;
    if (vendorId) {
      url += `&vendor_id=${vendorId}`;
    }
    if (categoryId) {
      url += `&category_id=${categoryId}`;
    }
    return api.get(url);
  },
  getCategoryWiseData: (timeRange = 'monthly', categoryId = null) => {
    let url = `/dashboard/charts/category-wise-data?time_range=${timeRange}`;
    if (categoryId) {
      url += `&category_id=${categoryId}`;
    }
    return api.get(url);
  },
  getVendorDistribution: () => api.get('/dashboard/charts/vendor-distribution'),
  getCategoryDistribution: () => api.get('/dashboard/charts/category-distribution'),
};

// Usage API calls
export const usageAPI = {
  getApiUsages: (skip = 0, limit = 100, sortBy = null, sortOrder = null, vendorId = null, searchTerm = null) => {
    let url = `/api-usages?skip=${skip}&limit=${limit}`;
    if (sortBy) {
      url += `&sort_by=${sortBy}`;
    }
    if (sortOrder) {
      url += `&sort_order=${sortOrder}`;
    }
    if (vendorId) {
      url += `&vendor_id=${vendorId}`;
    }
    if (searchTerm) {
      url += `&search_term=${encodeURIComponent(searchTerm)}`;
    }
    return api.get(url);
  },
  getApiUsagesCount: (vendorId = null, searchTerm = null) => {
    let url = '/api-usages/count';
    if (vendorId) {
      url += `?vendor_id=${vendorId}`;
    }
    if (searchTerm) {
      const separator = url.includes('?') ? '&' : '?';
      url += `${separator}search_term=${encodeURIComponent(searchTerm)}`;
    }
    return api.get(url);
  },
  getApiUsage: (id) => api.get(`/api-usages/${id}`),
  getApiUsagePdf: (id) => api.get(`/api-usages/${id}/pdf`, { responseType: 'blob' }),
  createApiUsage: (usageData) => api.post('/api-usages', usageData),
  updateUsage: (id, usageData) => api.put(`/api-usages/${id}`, usageData),
  deleteUsage: (id) => {
    // Clear any potential cached usage data from local storage before deletion
    const cachedUsageKeys = Object.keys(localStorage).filter(key => 
      key.startsWith('usage_') || key.startsWith('usages_')
    );
    cachedUsageKeys.forEach(key => localStorage.removeItem(key));
    
    return api.delete(`/api-usages/${id}`);
  },
};

// Rules API calls
export const rulesAPI = {
  getRules: () => api.get('/rules'),
  createRule: (ruleData) => api.post('/rules', ruleData),
  updateRule: (id, ruleData) => api.put(`/rules/${id}`, ruleData),
  deleteRule: (id) => api.delete(`/rules/${id}`),
};

// Activity Logs API calls
export const logAPI = {
  getActivityLogs: (skip = 0, limit = 100, resourceType = null, action = null) => {
    let url = `/logs?skip=${skip}&limit=${limit}`;
    if (resourceType) {
      url += `&resource_type=${resourceType}`;
    }
    if (action) {
      url += `&action=${action}`;
    }
    return api.get(url);
  },
  getActivityLog: (id) => api.get(`/logs/${id}`),
  getUserActivityLogs: (userId, skip = 0, limit = 100) => 
    api.get(`/logs/user/${userId}?skip=${skip}&limit=${limit}`),
};

export default api;